/**
 * Bundle 46 Task 2 - Data Health drill-down page.
 *
 * Renders when admin clicks a card's "Review N users" (or events)
 * button. Route: /admin/health/:cardId/rows
 *
 * Consumes GET /api/admin/health/:cardId/rows and renders as a table
 * with per-row and bulk actions. Zero JSON on screen.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, RefreshCw, Wrench } from 'lucide-react';
import api from '@/utils/api';

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(); } catch { return ''; }
};

const AdminHealthDrilldownPage = () => {
  const { cardId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState({});
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/health/${cardId}/rows`);
      setData(data);
      setSelected({});
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not load users.';
      toast.error(typeof msg === 'string' ? msg : 'Could not load.');
    } finally {
      setLoading(false);
    }
  }, [cardId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (rid) => setSelected((s) => ({ ...s, [rid]: !s[rid] }));
  const selectAll = () => {
    const all = {};
    (data?.rows || []).forEach((r) => { all[r.user_id || r.event_id] = true; });
    setSelected(all);
  };

  const runRow = async (r) => {
    if (!r.row_action?.endpoint) return;
    const key = r.user_id || r.event_id;
    setRowBusy((s) => ({ ...s, [key]: true }));
    try {
      await api.request({
        url: r.row_action.endpoint,
        method: (r.row_action.method || 'POST').toLowerCase(),
      });
      toast.success('Done.');
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Action failed.';
      toast.error(typeof msg === 'string' ? msg : 'Action failed.');
    } finally {
      setRowBusy((s) => ({ ...s, [key]: false }));
    }
  };

  const runSelected = async () => {
    const targets = (data?.rows || []).filter((r) => selected[r.user_id || r.event_id]);
    if (targets.length === 0) {
      toast.error('Select at least one row.');
      return;
    }
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const r of targets) {
      if (!r.row_action?.endpoint) { fail += 1; continue; }
      try {
        await api.request({
          url: r.row_action.endpoint,
          method: (r.row_action.method || 'POST').toLowerCase(),
        });
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    toast[fail ? 'warning' : 'success'](
      `Ran ${targets.length}: ${ok} succeeded${fail ? `, ${fail} failed` : ''}.`,
    );
    setBulkBusy(false);
    load();
  };

  const entity = data?.entity || 'users';
  const rows = data?.rows || [];

  return (
    <div className="p-8 max-w-5xl" data-testid={`admin-health-drilldown-${cardId}`}>
      <Link
        to="/admin/health"
        className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 mb-4"
        data-testid="admin-health-drilldown-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Data Health
      </Link>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-neutral-900">
            {data?.subtitle || 'Review'}
          </h1>
          <p className="mt-1 text-sm text-neutral-500">
            {loading ? 'Loading...' : `${rows.length} ${entity}`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm"
            onClick={load} disabled={loading}
            data-testid="admin-health-drilldown-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {rows.length > 0 && entity === 'users' ? (
            <>
              <Button
                variant="outline" size="sm"
                onClick={selectAll}
                data-testid="admin-health-drilldown-select-all"
              >
                Select all
              </Button>
              <Button
                size="sm"
                onClick={runSelected}
                disabled={bulkBusy || Object.values(selected).filter(Boolean).length === 0}
                data-testid="admin-health-drilldown-run-selected"
                className="bg-primary-900 hover:bg-primary-800 text-white"
              >
                {bulkBusy ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Working...</>
                ) : (
                  <><Wrench className="h-3.5 w-3.5 mr-1" /> Fix selected</>
                )}
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-neutral-500">Loading...</div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center">
            <div className="text-sm text-neutral-500">Nothing to review.</div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-neutral-500 border-b border-neutral-200">
                  <tr>
                    {entity === 'users' ? <th className="p-3 w-8"></th> : null}
                    <th className="p-3">
                      {entity === 'users' ? 'User' : 'Event'}
                    </th>
                    <th className="p-3">Problem</th>
                    <th className="p-3">Created</th>
                    <th className="p-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const key = r.user_id || r.event_id;
                    return (
                      <tr key={key} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`drilldown-row-${key}`}>
                        {entity === 'users' ? (
                          <td className="p-3">
                            <Checkbox
                              checked={!!selected[key]}
                              onCheckedChange={() => toggle(key)}
                              data-testid={`drilldown-row-select-${key}`}
                            />
                          </td>
                        ) : null}
                        <td className="p-3">
                          {entity === 'users' ? (
                            <Link
                              to={`/admin/users/${r.user_id}`}
                              className="font-medium text-primary-900 hover:underline"
                            >
                              {r.display_name}
                            </Link>
                          ) : (
                            <span className="font-medium">{r.event_name}</span>
                          )}
                          <div className="text-xs text-neutral-500">
                            {r.email || r.user_id || r.event_id}
                          </div>
                        </td>
                        <td className="p-3 text-neutral-700 max-w-md">
                          {r.problem}
                        </td>
                        <td className="p-3 text-neutral-500 whitespace-nowrap">
                          {fmtDate(r.created_at)}
                        </td>
                        <td className="p-3 text-right">
                          {r.row_action?.endpoint ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => runRow(r)}
                              disabled={rowBusy[key]}
                              data-testid={`drilldown-row-action-${key}`}
                            >
                              {rowBusy[key] ? (
                                <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> ...</>
                              ) : (
                                r.row_action.label
                              )}
                            </Button>
                          ) : (
                            <span className="text-xs text-neutral-400">
                              {r.row_action?.label || '\u2014'.replace('\u2014', '-')}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminHealthDrilldownPage;
